import { makeObservable, observable, action, computed } from 'mobx';
import CodelistStore from '../../../global/stores/CodelistStore';
import { IAttachment } from '../../../global/types/types';
import { IDeviceRow } from '../../../swagger/gd/codelist';

/** Minimal UUID v4 generator — avoids needing @types/uuid */
export const uuidv4 = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

// ─── Internal UI types ───────────────────────────────────────────────────────
// These extend the swagger types with UI-specific fields (_id, city name, deviceCode)
export interface IDeviceEntry {
  _id: string;
  deviceId: string; // IDeviceRow._id
  deviceCode: string; // IDeviceRow.code_ext
  device: string; // IDeviceRow.name_ext — display name
  power: number;
  capacity?: number;
  catastral?: string;
  lot_number?: string;
  // Zariadenie už nainštalované (reimbursement) — prevzaté z IDeviceRow v čase pridania
  deviceTypeName?: string;
  manufacturer?: string;
  modelType?: string;
  // Počet — manuálny vstup, len pri reimbursement toku
  count?: number;
  // Prílohy k nainštalovanému zariadeniu — len pri reimbursement toku
  installationPhotos?: IAttachment[];
  connectionContract?: IAttachment[];
  ownerConsent?: IAttachment[];
  leaseContract?: IAttachment[];
  // Fotka výrobného štítku — vyplnená z InstalledDeviceSelectDialog ("Vybrať zariadenie")
  labelPhotos?: IAttachment[];
}

// Nainštalované zariadenie (reimbursement tok) — vybrané cez InstalledDeviceSelectDialog
// (z niektorej zo 4 kategorizovaných tabuliek v DeviceDialog), zodpovedá installed_devices_data
export interface IInstalledDeviceEntry {
  _id: string;
  energeticDeviceId: string; // ID záznamu z POST /gd/record/energetic-device
  count: number;
  attachments?: IAttachment[];
  // Zobrazovacie polia pre tabuľku miest v RequestForm (rovnaké stĺpce ako pri IDeviceEntry)
  deviceTypeName?: string;
  manufacturer?: string;
  modelType?: string;
  // Skryté polia — kódy/id zvolených Druh/Špecifikácia/Výrobca, pre predvyplnenie
  // InstalledDeviceSelectDialog pri neskoršej editácii riadku (viď RequestForm — tabuľka miest)
  typeCode?: string;
  specificationId?: string;
  manufacturerCode?: string;
}

export interface IPlaceEntry {
  cityId: string; // ICityRow._id / code_ext used as identifier
  city: string; // display name
  street: string;
  catastral?: string;
  lot_number?: string;
  // BE project_implementation_id — nastavené len pri načítaní už uloženého miesta realizácie
  // existujúcej žiadosti; pri uložení sa posiela naspäť, aby BE vykonal update tohto miesta
  // namiesto vytvorenia nového (nové miesto pridané vo formulári ho nemá — vytvorí sa nové).
  projectImplementationId?: string;
  devices: IDeviceEntry[];
  installedDevices: IInstalledDeviceEntry[];
}

// ─── Dialog form values ──────────────────────────────────────────────────────
export interface IDeviceDialogForm {
  cityId: string;
  street: string;
  device: IDeviceRow | null; // full object for display + code resolution
  // Plochý kód zariadenia (device.code_ext) — slúži ako spoľahlivý podmienkový kľúč
  // pre Joi.when (XsSearchSelect.postAction ho synchronizuje s `device`)
  deviceCode: string;
  devicePower: number | '';
  deviceCapacity?: number | '';
  deviceCadastralArea?: string;
  deviceParcelNumber?: string;
  deviceCount?: number | '';
  deviceInstallationPhotos?: IAttachment[];
  deviceConnectionContract?: IAttachment[];
  deviceOwnerConsent?: IAttachment[];
  deviceLeaseContract?: IAttachment[];
  deviceLabelPhotos?: IAttachment[];
}

const MAX_VOUCHER = 50_000;

// Codes that require capacity field
export const DEVICES_WITH_CAPACITY = ['WTWA', 'HPWA', 'SCWA', 'PVWA'];
// Codes that require cadastral + parcel fields
export const DEVICES_WITH_CATASTRAL = ['PVNA', 'PVWA'];

class DeviceDialogStore {
  isOpen: boolean = false;
  onlyDevice: boolean = false;
  editIndex: number | null = null; // index of place being edited (null = new)
  places: IPlaceEntry[] = [];
  assumedVoucherValue: number = 0;
  // Pre-fill values when opening in onlyDevice mode (city locked)
  prefillCity: { cityId: string; city: string; street: string; catastral?: string; lot_number?: string } | null = null;
  // Názov obce vybraný v autocomplete dialógu (autocomplete drží len cityId) — použije sa pri addDevice
  pendingCityName: string = '';
  // region_type_id zvoleného kola — filter obcí v autocomplete (verný port addSpecialFilter)
  regionTypeId: string = '';
  // round_id zvoleného kola — filter zariadení v dropdowne (verný port loadDropdownDevices)
  roundId: string = '';
  // devide_group_id zvoleného kola ('ele' | 'heat') — riadi, ktoré sekcie (menič/akumulátor
  // vs. akumulačná nádrž) sa v dialógu zobrazujú pri reimbursement toku
  deviceGroupId: string = '';
  // Zariadenia ponúkané v dropdowne, filtrované podľa round_id (server-side)
  dropdownDevices: IDeviceRow[] = [];

  constructor() {
    makeObservable(this, {
      isOpen: observable,
      onlyDevice: observable,
      editIndex: observable,
      places: observable,
      assumedVoucherValue: observable,
      prefillCity: observable,
      pendingCityName: observable,
      regionTypeId: observable,
      roundId: observable,
      deviceGroupId: observable,
      dropdownDevices: observable,
      setRegionTypeId: action,
      setRoundId: action,
      setDeviceGroupId: action,
      setDropdownDevices: action,
      open: action,
      close: action,
      addDevice: action,
      addInstalledDevices: action,
      editDevice: action,
      removePlace: action,
      removeDevice: action,
      removeInstalledDevice: action,
      updateInstalledDevice: action,
      replaceDevicesWithInstalled: action,
      computeAssumedValue: action,
      setPendingCityName: action,
      setPlaces: action,
      reset: action,
      allDevices: computed
    });
  }

  /** Zapamätá názov obce vybranej v autocomplete (cityId → názov) pre zobrazenie v zozname miest. */
  setPendingCityName = (name: string) => {
    this.pendingCityName = name;
  };

  setRegionTypeId = (id: string) => {
    this.regionTypeId = id;
  };

  setRoundId = (id: string) => {
    this.roundId = id;
  };

  setDeviceGroupId = (id: string) => {
    this.deviceGroupId = id;
  };

  setDropdownDevices = (devices: IDeviceRow[]) => {
    this.dropdownDevices = devices;
  };

  /** Nastaví zoznam miest naraz (použité pri načítaní existujúcej žiadosti). */
  setPlaces = (places: IPlaceEntry[]) => {
    this.places = places;
    this.computeAssumedValue();
  };

  /** Returns flat list of all devices across all places (for overview / debugging) */
  get allDevices(): IDeviceEntry[] {
    return this.places.flatMap((p) => p.devices);
  }

  /**
   * Open the dialog.
   * @param place  If supplied the city/street fields are pre-filled and locked (onlyDevice mode).
   */
  open = (place?: IPlaceEntry) => {
    if (place) {
      this.onlyDevice = true;
      this.prefillCity = {
        cityId: place.cityId,
        city: place.city,
        street: place.street,
        catastral: place.catastral,
        lot_number: place.lot_number
      };
    } else {
      this.onlyDevice = false;
      this.prefillCity = null;
    }
    this.editIndex = null;
    this.isOpen = true;
  };

  close = () => {
    this.isOpen = false;
    this.prefillCity = null;
    this.editIndex = null;
  };

  /**
   * Add a new device entry (called from DeviceDialog onSubmit).
   * Groups by cityId + street, creates a new place if not found.
   */
  addDevice = (values: IDeviceDialogForm) => {
    const deviceRow = values.device!;

    const newDevice: IDeviceEntry = {
      _id: uuidv4(),
      deviceId: deviceRow._id,
      deviceCode: deviceRow.code_ext ?? '',
      device: deviceRow.name_ext ?? '',
      power: Number(values.devicePower),
      capacity:
        values.deviceCapacity !== '' && values.deviceCapacity != null ? Number(values.deviceCapacity) : undefined,
      catastral: values.deviceCadastralArea || undefined,
      lot_number: values.deviceParcelNumber || undefined,
      deviceTypeName: deviceRow.device_type_name || undefined,
      manufacturer: deviceRow.manufacturer || undefined,
      modelType: deviceRow.model_type || undefined,
      count: values.deviceCount !== '' && values.deviceCount != null ? Number(values.deviceCount) : undefined,
      installationPhotos: values.deviceInstallationPhotos,
      connectionContract: values.deviceConnectionContract,
      ownerConsent: values.deviceOwnerConsent,
      leaseContract: values.deviceLeaseContract,
      labelPhotos: values.deviceLabelPhotos
    };

    const streetNorm = (values.street ?? '').trim();
    const existingPlace = this.places.find((p) => p.cityId === values.cityId && p.street === streetNorm);

    if (existingPlace) {
      existingPlace.devices.push(newDevice);
    } else {
      this.places.push({
        cityId: values.cityId,
        city: this.prefillCity?.city ?? this.pendingCityName ?? '',
        street: streetNorm,
        catastral: values.deviceCadastralArea || '',
        lot_number: values.deviceParcelNumber || '',
        devices: [newDevice],
        installedDevices: []
      });
    }
    this.pendingCityName = '';

    this.computeAssumedValue();
    this.close();
  };

  /**
   * Add installed device entries (reimbursement tok, called from DeviceDialog onSubmit).
   * Groups by cityId + street rovnako ako addDevice; ak entries je prázdne, nič nerobí a
   * dialóg zostáva otvorený (vynucuje pridanie aspoň jedného zariadenia pred uložením).
   */
  addInstalledDevices = (
    cityId: string,
    street: string,
    entries: IInstalledDeviceEntry[],
    catastral?: string,
    lot_number?: string
  ) => {
    if (entries.length === 0) return;

    const streetNorm = (street ?? '').trim();
    const existingPlace = this.places.find((p) => p.cityId === cityId && p.street === streetNorm);

    if (existingPlace) {
      existingPlace.installedDevices.push(...entries);
      // Katastrálne územie/parcela sa v reimbursement toku zbierajú nanovo pri každom otvorení
      // dialógu (required polia) — pri doplnení do existujúceho miesta (conf-reserv tok) sa preto
      // musia prepísať, inak by ostali prázdne/pôvodné z rezervácie namiesto práve zadaných.
      if (catastral) existingPlace.catastral = catastral;
      if (lot_number) existingPlace.lot_number = lot_number;
    } else {
      this.places.push({
        cityId,
        city: this.prefillCity?.city ?? this.pendingCityName ?? '',
        street: streetNorm,
        catastral: catastral || '',
        lot_number: lot_number || '',
        devices: [],
        installedDevices: entries
      });
    }
    this.pendingCityName = '';

    this.computeAssumedValue();
    this.close();
  };

  /**
   * "Doplniť inštalované zariadenia" (conf-reserv tok) — miesto po doplnení inštalovaných
   * zariadení prejde z pôvodných rezervovaných devices na installed_devices_data.
   */
  replaceDevicesWithInstalled = (place: IPlaceEntry) => {
    place.devices = [];
    this.computeAssumedValue();
  };

  /**
   * Update city display name after autocomplete selection.
   * Called from DeviceDialog's onApply callback.
   */
  setCityName = (cityId: string, street: string, cityName: string) => {
    const place = this.places.find((p) => p.cityId === cityId && p.street === street);
    if (place) place.city = cityName;
  };

  /** Update an existing device entry in-place. */
  editDevice = (placeIdx: number, deviceId: string, values: IDeviceDialogForm) => {
    const place = this.places[placeIdx];
    if (!place) return;

    const devIdx = place.devices.findIndex((d) => d._id === deviceId);
    if (devIdx < 0) return;

    const deviceRow = values.device!;
    place.devices[devIdx] = {
      _id: deviceId,
      deviceId: deviceRow._id,
      deviceCode: deviceRow.code_ext ?? '',
      device: deviceRow.name_ext ?? '',
      power: Number(values.devicePower),
      capacity:
        values.deviceCapacity !== '' && values.deviceCapacity != null ? Number(values.deviceCapacity) : undefined,
      catastral: values.deviceCadastralArea || undefined,
      lot_number: values.deviceParcelNumber || undefined,
      deviceTypeName: deviceRow.device_type_name || undefined,
      manufacturer: deviceRow.manufacturer || undefined,
      modelType: deviceRow.model_type || undefined,
      count: values.deviceCount !== '' && values.deviceCount != null ? Number(values.deviceCount) : undefined,
      installationPhotos: values.deviceInstallationPhotos,
      connectionContract: values.deviceConnectionContract,
      ownerConsent: values.deviceOwnerConsent,
      leaseContract: values.deviceLeaseContract,
      labelPhotos: values.deviceLabelPhotos
    };

    this.computeAssumedValue();
    this.close();
  };

  /** Remove a whole place (all its devices). */
  removePlace = (place: IPlaceEntry) => {
    this.places = this.places.filter((p) => !(p.cityId === place.cityId && p.street === (place.street ?? '').trim()));
    this.computeAssumedValue();
  };

  /** Remove a single device; if the place becomes empty it is also removed. */
  removeDevice = (place: IPlaceEntry, device: IDeviceEntry) => {
    this.places = this.places
      .map((p) => {
        if (p.cityId !== place.cityId || p.street !== (place.street ?? '').trim()) {
          return p;
        }
        return { ...p, devices: p.devices.filter((d) => d._id !== device._id) };
      })
      .filter((p) => p.devices.length > 0 || p.installedDevices.length > 0);

    this.computeAssumedValue();
  };

  /** Remove a single installed device entry; if the place becomes empty it is also removed. */
  removeInstalledDevice = (place: IPlaceEntry, entry: IInstalledDeviceEntry) => {
    this.places = this.places
      .map((p) => {
        if (p.cityId !== place.cityId || p.street !== (place.street ?? '').trim()) {
          return p;
        }
        return { ...p, installedDevices: p.installedDevices.filter((d) => d._id !== entry._id) };
      })
      .filter((p) => p.devices.length > 0 || p.installedDevices.length > 0);

    this.computeAssumedValue();
  };

  /** Update an existing installed device entry in-place (keeps its _id); place matched by cityId + street. */
  updateInstalledDevice = (place: IPlaceEntry, entryId: string, entry: Omit<IInstalledDeviceEntry, '_id'>) => {
    const targetPlace = this.places.find((p) => p.cityId === place.cityId && p.street === (place.street ?? '').trim());
    if (!targetPlace) return;

    const idx = targetPlace.installedDevices.findIndex((d) => d._id === entryId);
    if (idx < 0) return;

    targetPlace.installedDevices[idx] = { ...entry, _id: entryId };
    this.computeAssumedValue();
  };

  /**
   * Recalculate the expected voucher amount.
   * Formula: sum(power × power_unit_price) × 0.45 , capped at 50 000 €.
   * The contributionForEnergyAudit (+2500 €) is handled in RequestFormStore
   * because it lives on the main form — we only compute the device-based sum here.
   */
  computeAssumedValue = () => {
    let total = 0;
    const deviceCL: IDeviceRow[] = CodelistStore.getCodelist<IDeviceRow>('device');

    this.places.forEach((place) => {
      place.devices.forEach((dev) => {
        const cl = deviceCL.find((x) => x.code_ext === dev.deviceCode);
        const unitPrice = cl?.power_unit_price ?? 0;
        total += dev.power * unitPrice;
      });
    });

    const voucher = Math.floor(total * 0.45);
    this.assumedVoucherValue = voucher <= MAX_VOUCHER ? voucher : MAX_VOUCHER;
  };

  reset = () => {
    this.isOpen = false;
    this.onlyDevice = false;
    this.editIndex = null;
    this.places = [];
    this.assumedVoucherValue = 0;
    this.prefillCity = null;
    this.pendingCityName = '';
    this.regionTypeId = '';
    this.roundId = '';
    this.deviceGroupId = '';
    this.dropdownDevices = [];
  };
}

const singleton = new DeviceDialogStore();
export default singleton;
